import React, { useState, useEffect } from 'react'

interface CountdownTimerProps {
  deadline: string // Date string in format YYYY-MM-DD
}

export default function CountdownTimer({ deadline }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())

  function calculateTimeLeft() {
    const deadlineDate = new Date(deadline + 'T23:59:59') // End of day
    const now = new Date()
    const diff = deadlineDate.getTime() - now.getTime()

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, isOverdue: true }
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

    return { days, hours, minutes, isOverdue: false }
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [deadline])

  if (timeLeft.isOverdue) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium text-red-900">
          Deadline passed
        </span>
      </div>
    )
  }

  const { days, hours, minutes } = timeLeft
  const isUrgent = days < 3

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${
      isUrgent
        ? 'bg-orange-50 border-orange-200'
        : 'bg-indigo-50 border-indigo-200'
    }`}>
      <svg className={`w-5 h-5 ${isUrgent ? 'text-orange-600' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div className="text-sm">
        {days > 0 && (
          <span className={`font-semibold ${isUrgent ? 'text-orange-900' : 'text-indigo-900'}`}>
            {days} {days === 1 ? 'day' : 'days'}{hours > 0 || minutes > 0 ? ', ' : ''}
          </span>
        )}
        {days === 0 && hours > 0 && (
          <span className={`font-semibold ${isUrgent ? 'text-orange-900' : 'text-indigo-900'}`}>
            {hours} {hours === 1 ? 'hour' : 'hours'}{minutes > 0 ? ', ' : ''}
          </span>
        )}
        {days === 0 && hours === 0 && (
          <span className={`font-semibold ${isUrgent ? 'text-orange-900' : 'text-indigo-900'}`}>
            {minutes} {minutes === 1 ? 'minute' : 'minutes'}
          </span>
        )}
        <span className={`${isUrgent ? 'text-orange-700' : 'text-indigo-700'} ml-1`}>
          until deadline
        </span>
      </div>
    </div>
  )
}
